import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { axiosErrorHandler } from 'common/helpers/axios.helper';
import { LIST_LIMIT_5 } from 'common/config';
import { AppDispatch, RootState } from 'app/store';
import {
  ICreateUserAssigneePayload,
  IDeleteUserAssigneePayload,
  IUser,
  IAddUserSubdivisionPayload,
  IUserAssignee,
  IUserAssigneesParams,
  IChangeUserEmailPayload,
  IChangeUserPasswordByCodePayload,
  IChangeUserPhonePayload,
  IConfirmUserEmailPayload,
  IConfirmUserPhonePayload,
  IDeleteUserPayload,
  IUserInventoryStats,
  IUserInventoryStatsParams,
  IUserListParams,
  IUserParams,
  IRemoveUserSubdivisionPayload,
  ISendChangeUserPasswordCodePayload,
  IUserStats,
  IUpdateUserByAdminPayload,
  IUpdateUserPayload,
} from 'entities/User/User.models';
import { userTransport } from 'entities/User/User.transport';
import { IContract, IContractListParams } from 'entities/Contract/Contract.models';
import { contractTransport } from 'entities/Contract/Contract.transport';
import { addSubdivisionUsersItem, removeSubdivisionUsersItem } from 'entities/Subdivision/Subdivision.slice';
import { ISubdivision } from 'entities/Subdivision/Subdivision.models';
import { IWorkspace } from 'entities/Workspace/Workspace.models';

interface IState {
  currentUser: IUser | null;
  user: IUser | null;
  userList: IUser[];
  userCount: number;
  isUserLoading: boolean;
  userError: string | null;
  userListWithLimit: IUser[];
  userListWithLimitCount: number;
  userContracts: IContract[];
  userAssignees: IUserAssignee[];
  userStats: IUserStats;
  userInventoryStats: IUserInventoryStats;
}

const initialState: IState = {
  currentUser: null,
  user: null,
  userList: [],
  userCount: 0,
  isUserLoading: false,
  userError: null,
  userListWithLimit: [],
  userListWithLimitCount: 0,
  userContracts: [],
  userAssignees: [],
  userStats: {
    requestsCount: 0,
    basketGoodsCount: 0,
    sellerRequestsCount: 0,
  },
  userInventoryStats: {
    inventoryAvailableCount: 0,
    inventoryWrittenOffCount: 0,
    incomingTransfersCount: 0,
    outcomingTransfersCount: 0,
    writeOffsCount: 0,
  },
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setCurrentUser(state, action: PayloadAction<IUser | null>) {
      state.currentUser = action.payload;
    },
    setUser(state, action: PayloadAction<IUser | null>) {
      state.user = action.payload;

      if (state.currentUser?.id === action.payload?.id) {
        state.currentUser = action.payload;
      }
    },
    setUserList(state, action: PayloadAction<IUser[]>) {
      state.userList = action.payload;
    },
    setUserListChunk(state, action: PayloadAction<IUser[]>) {
      state.userList.push(...action.payload);
    },
    setUserCount(state, action: PayloadAction<number>) {
      state.userCount = action.payload;
    },
    setIsUserLoading(state, action: PayloadAction<boolean>) {
      state.isUserLoading = action.payload;
    },
    setUserError(state, action: PayloadAction<string | null>) {
      state.userError = action.payload;
    },
    setUserListWithLimit(state, action: PayloadAction<IUser[]>) {
      state.userListWithLimit = action.payload;
    },
    setUserListWithLimitChunk(state, action: PayloadAction<IUser[]>) {
      state.userListWithLimit.push(...action.payload);
    },
    setUserListWithLimitCount(state, action: PayloadAction<number>) {
      state.userListWithLimitCount = action.payload;
    },
    patchUserListItem(state, action: PayloadAction<IUser>) {
      const item = state.userList.find((i) => i.id === action.payload.id);

      if (!item) {
        return;
      }

      Object.assign(item, action.payload);
    },
    removeUserListItem(state, action: PayloadAction<number>) {
      state.userList = state.userList.filter((item) => item.id !== action.payload);
      state.userCount -= 1;
    },
    addUserListWithLimitItem(state, action: PayloadAction<IUser>) {
      state.userListWithLimit.push(action.payload);
    },
    patchUserListWithLimitItem(state, action: PayloadAction<IUser>) {
      const item = state.userListWithLimit.find((i) => i.id === action.payload.id);

      if (!item) {
        return;
      }

      Object.assign(item, action.payload);
    },
    removeUserListWithLimitItem(state, action: PayloadAction<number>) {
      state.userListWithLimit = state.userListWithLimit.filter((item) => item.id !== action.payload);
    },
    setUserContracts(state, action: PayloadAction<IContract[]>) {
      state.userContracts = action.payload;
    },
    setUserAssignees(state, action: PayloadAction<IUserAssignee[]>) {
      state.userAssignees = action.payload;
    },
    addUserAssigneesItem(state, action: PayloadAction<IUserAssignee>) {
      state.userAssignees.push(action.payload);
    },
    removeUserAssigneesItem(state, action: PayloadAction<number>) {
      state.userAssignees = state.userAssignees.filter((item) => item.id !== action.payload);
    },
    patchCurrentUserAccountName(state, action: PayloadAction<string>) {
      if (!state.currentUser) {
        return;
      }

      if (state.currentUser.account) {
        state.currentUser.account.name = action.payload;
      }
    },
    patchCurrentUserSubdivision(state, action: PayloadAction<ISubdivision>) {
      if (!state.currentUser) {
        return;
      }

      const item = state.currentUser.subdivisions?.find((i) => i.id === action.payload.id);

      if (!item) {
        return;
      }

      Object.assign(item, action.payload);
    },
    addCurrentUserWorkspacesItem(state, action: PayloadAction<IWorkspace>) {
      if (!state.currentUser || state.currentUser.id !== action.payload.user?.id) {
        return;
      }

      const workspaces = (state.currentUser.workspaces ??= []);
      workspaces.push(action.payload);
    },
    patchCurrentUserWorkspacesItem(state, action: PayloadAction<IWorkspace>) {
      if (!state.currentUser) {
        return;
      }

      const currentUserWorkspaces = state.currentUser.workspaces;

      if (state.currentUser.id !== action.payload.user?.id) {
        if (!currentUserWorkspaces) {
          return;
        }

        state.currentUser.workspaces = currentUserWorkspaces.filter((item) => item.id !== action.payload.id);
        return;
      }

      if (!currentUserWorkspaces) {
        state.currentUser.workspaces = [action.payload];
        return;
      }

      const existingWorkspace = currentUserWorkspaces.find((item) => item.id === action.payload.id);

      if (existingWorkspace) {
        Object.assign(existingWorkspace, action.payload);
      } else {
        currentUserWorkspaces.push(action.payload);
      }
    },
    removeCurrentUserWorkspacesItem(state, action: PayloadAction<IWorkspace>) {
      if (!state.currentUser || state.currentUser.id !== action.payload.user?.id) {
        return;
      }

      if (!state.currentUser.workspaces) {
        return;
      }

      state.currentUser.workspaces = state.currentUser.workspaces.filter((item) => item.id !== action.payload.id);
    },
    setUserStats(state, action: PayloadAction<IUserStats>) {
      state.userStats = action.payload;
    },
    setUserInventoryStats(state, action: PayloadAction<IUserInventoryStats>) {
      state.userInventoryStats = action.payload;
    },
  },
});

export const {
  setCurrentUser,
  setUser,
  setUserList,
  setUserListChunk,
  setUserCount,
  setIsUserLoading,
  setUserError,
  setUserListWithLimit,
  setUserListWithLimitChunk,
  setUserListWithLimitCount,
  patchUserListItem,
  removeUserListItem,
  addUserListWithLimitItem,
  patchUserListWithLimitItem,
  removeUserListWithLimitItem,
  setUserContracts,
  setUserAssignees,
  addUserAssigneesItem,
  removeUserAssigneesItem,
  patchCurrentUserAccountName,
  patchCurrentUserSubdivision,
  addCurrentUserWorkspacesItem,
  patchCurrentUserWorkspacesItem,
  removeCurrentUserWorkspacesItem,
  setUserStats,
  setUserInventoryStats,
} = userSlice.actions;

export const selectCurrentUser = (state: RootState) => state.user.currentUser;
export const selectUser = (state: RootState) => state.user.user;
export const selectUserList = (state: RootState) => state.user.userList;
export const selectUserCount = (state: RootState) => state.user.userCount;
export const selectIsUserLoading = (state: RootState) => state.user.isUserLoading;
export const selectUserError = (state: RootState) => state.user.userError;
export const selectUserListWithLimit = (state: RootState) => state.user.userListWithLimit;
export const selectUserListWithLimitCount = (state: RootState) => state.user.userListWithLimitCount;
export const selectUserContracts = (state: RootState) => state.user.userContracts;
export const selectUserAssignees = (state: RootState) => state.user.userAssignees;
export const selectUserStats = (state: RootState) => state.user.userStats;
export const selectUserInventoryStats = (state: RootState) => state.user.userInventoryStats;

export const userReducer = userSlice.reducer;

export const getUserList =
  ({ onSuccess, ...params }: IUserListParams) =>
  async (dispatch: AppDispatch) => {
    dispatch(setIsUserLoading(true));

    try {
      const response = await userTransport.get(params);
      dispatch(setUserList(response.data));
      dispatch(setUserCount(response.count));
      onSuccess?.();
      return response.data;
    } catch (error) {
      axiosErrorHandler(error);
      return null;
    } finally {
      dispatch(setIsUserLoading(false));
    }
  };

export const getUserListChunk = (params: IUserListParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsUserLoading(true));

  try {
    const response = await userTransport.get(params);
    dispatch(setUserListChunk(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsUserLoading(false));
  }
};

export const getUsersWithLimit =
  ({ onSuccess, ...params }: IUserListParams) =>
  async (dispatch: AppDispatch) => {
    dispatch(setIsUserLoading(true));

    try {
      const response = await userTransport.get(params);
      dispatch(setUserListWithLimit(response.data));
      dispatch(setUserListWithLimitCount(response.count));
      onSuccess?.();
    } catch (error) {
      axiosErrorHandler(error);
    } finally {
      dispatch(setIsUserLoading(false));
    }
  };

export const getUsersWithLimitChunk = (params: IUserListParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsUserLoading(true));

  try {
    const response = await userTransport.get(params);
    dispatch(setUserListWithLimitChunk(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsUserLoading(false));
  }
};

export const getCurrentUser = (id: number) => async (dispatch: AppDispatch) => {
  dispatch(setIsUserLoading(true));

  try {
    const response = await userTransport.getById(id);
    dispatch(setCurrentUser(response));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsUserLoading(false));
  }
};

export const getUserById = (params: IUserParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsUserLoading(true));

  try {
    const response = await userTransport.getById(params.id);
    dispatch(setUser(response));
    params.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsUserLoading(false));
  }
};

// Точечный поиск юзера по email без записи в общий список (используется после приглашения,
// чтобы открыть карточку только что созданного пользователя — InvitationDto не отдаёт его id).
export const getUserByEmail = (email: string, accountId?: number) => async (): Promise<IUser | null> => {
  try {
    const response = await userTransport.get({ accountId, search: email, limit: LIST_LIMIT_5 });

    return response.data.find((user) => user.email === email) ?? null;
  } catch (error) {
    axiosErrorHandler(error);

    return null;
  }
};

export const updateUser =
  ({ onSuccess, ...payload }: IUpdateUserPayload) =>
  async (dispatch: AppDispatch): Promise<IUser | null> => {
    try {
      const response = await userTransport.update(payload);
      dispatch(setUser(response));
      onSuccess?.();
      return response;
    } catch (error) {
      axiosErrorHandler(error);
      return null;
    }
  };

export const updateUserByAdmin = (payload: IUpdateUserByAdminPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsUserLoading(true));

  try {
    const response = await userTransport.updateByAdmin(payload);
    dispatch(patchUserListItem(response));
    dispatch(setUser(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsUserLoading(false));
  }
};

export const deleteUser = (payload: IDeleteUserPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsUserLoading(true));

  try {
    const response = await userTransport.delete(payload);
    dispatch(removeUserListItem(response.id));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsUserLoading(false));
  }
};

export const sendChangeUserPasswordCodeByEmail =
  (payload: ISendChangeUserPasswordCodePayload) => async (dispatch: AppDispatch) => {
    dispatch(setIsUserLoading(true));

    try {
      await userTransport.sendChangePasswordCodeByEmail();
      payload.onSuccess?.();
    } catch (error) {
      axiosErrorHandler(error, setUserError);
    } finally {
      dispatch(setIsUserLoading(false));
    }
  };

export const sendChangeUserPasswordCodeByPhone =
  (payload: ISendChangeUserPasswordCodePayload) => async (dispatch: AppDispatch) => {
    dispatch(setIsUserLoading(true));

    try {
      await userTransport.sendChangePasswordCodeByPhone();
      payload.onSuccess?.();
    } catch (error) {
      axiosErrorHandler(error, setUserError);
    } finally {
      dispatch(setIsUserLoading(false));
    }
  };

export const changeUserPasswordByEmailCode = (payload: IChangeUserPasswordByCodePayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsUserLoading(true));

  try {
    const response = await userTransport.changePasswordByEmailCode(payload);
    dispatch(setUser(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error, setUserError);
  } finally {
    dispatch(setIsUserLoading(false));
  }
};

export const changeUserPasswordByPhoneCode = (payload: IChangeUserPasswordByCodePayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsUserLoading(true));

  try {
    const response = await userTransport.changePasswordByPhoneCode(payload);
    dispatch(setUser(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error, setUserError);
  } finally {
    dispatch(setIsUserLoading(false));
  }
};

export const changeUserEmail =
  ({ onSuccess, ...rest }: IChangeUserEmailPayload) =>
  async (dispatch: AppDispatch) => {
    dispatch(setIsUserLoading(true));

    try {
      await userTransport.changeEmail(rest);
      onSuccess?.();
    } catch (error) {
      axiosErrorHandler(error, setUserError);
    } finally {
      dispatch(setIsUserLoading(false));
    }
  };

export const changeUserPhone =
  ({ onSuccess, ...rest }: IChangeUserPhonePayload) =>
  async (dispatch: AppDispatch) => {
    dispatch(setIsUserLoading(true));

    try {
      await userTransport.changePhone(rest);
      onSuccess?.();
    } catch (error) {
      axiosErrorHandler(error, setUserError);
    } finally {
      dispatch(setIsUserLoading(false));
    }
  };

export const confirmUserEmail =
  ({ onSuccess, ...rest }: IConfirmUserEmailPayload) =>
  async (dispatch: AppDispatch) => {
    dispatch(setIsUserLoading(true));

    try {
      await userTransport.confirmEmail(rest);
      onSuccess?.();
    } catch (error) {
      axiosErrorHandler(error, setUserError);
    } finally {
      dispatch(setIsUserLoading(false));
    }
  };

export const confirmUserPhone =
  ({ onSuccess, ...rest }: IConfirmUserPhonePayload) =>
  async (dispatch: AppDispatch) => {
    dispatch(setIsUserLoading(true));

    try {
      await userTransport.confirmPhone(rest);
      onSuccess?.();
    } catch (error) {
      axiosErrorHandler(error, setUserError);
    } finally {
      dispatch(setIsUserLoading(false));
    }
  };

export const getUserContracts = (params: IContractListParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsUserLoading(true));

  try {
    const response = await contractTransport.get(params);
    dispatch(setUserContracts(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsUserLoading(false));
  }
};

export const addUserSubdivision = (payload: IAddUserSubdivisionPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsUserLoading(true));

  try {
    const response = await userTransport.addSubdivision(payload);
    dispatch(setUser(response));
    dispatch(addSubdivisionUsersItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsUserLoading(false));
  }
};

export const removeUserSubdivision = (payload: IRemoveUserSubdivisionPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsUserLoading(true));

  try {
    const response = await userTransport.removeSubdivision(payload);
    dispatch(setUser(response));
    dispatch(removeSubdivisionUsersItem(response.id));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsUserLoading(false));
  }
};

export const createUserWithLimit = (payload: IUpdateUserByAdminPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsUserLoading(true));

  try {
    const response = await userTransport.updateByAdmin(payload);
    dispatch(addUserListWithLimitItem(response));
    dispatch(setCurrentUser(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsUserLoading(false));
  }
};

export const updateUserWithLimit = (payload: IUpdateUserByAdminPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsUserLoading(true));

  try {
    const response = await userTransport.updateByAdmin(payload);
    dispatch(patchUserListWithLimitItem(response));
    dispatch(setCurrentUser(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsUserLoading(false));
  }
};

export const deleteUserWithLimit = (payload: IUpdateUserByAdminPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsUserLoading(true));

  try {
    const response = await userTransport.updateByAdmin(payload);
    dispatch(removeUserListWithLimitItem(response.id));
    dispatch(setCurrentUser(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsUserLoading(false));
  }
};

export const getUserAssignees = (params: IUserAssigneesParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsUserLoading(true));

  try {
    const response = await userTransport.getAssignees(params);
    dispatch(setUserAssignees(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsUserLoading(false));
  }
};

export const createUserAssignee = (payload: ICreateUserAssigneePayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsUserLoading(true));

  try {
    const response = await userTransport.createAssignee(payload);
    dispatch(addUserAssigneesItem(response));
    payload.onSuccess?.();
    return response;
  } catch (error) {
    axiosErrorHandler(error);
    return null;
  } finally {
    dispatch(setIsUserLoading(false));
  }
};

export const deleteUserAssignee = (payload: IDeleteUserAssigneePayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsUserLoading(true));

  try {
    const response = await userTransport.deleteAssignee(payload);
    dispatch(removeUserAssigneesItem(response.id));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsUserLoading(false));
  }
};

export const getUserStats = () => async (dispatch: AppDispatch) => {
  dispatch(setIsUserLoading(true));

  try {
    const response = await userTransport.getStats();
    dispatch(setUserStats(response));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsUserLoading(false));
  }
};

export const getUserInventoryStats = (params: IUserInventoryStatsParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsUserLoading(true));

  try {
    const response = await userTransport.getInventoryStats(params);
    dispatch(setUserInventoryStats(response));
    return response;
  } catch (error) {
    axiosErrorHandler(error);
    return null;
  } finally {
    dispatch(setIsUserLoading(false));
  }
};
